<!--
 * @Author: Tahiti
 * @Date: 2025-12-04 16:22:39
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-04 17:23:56
 * @Description: 下拉菜单组件 - 支持自定义触发按钮和菜单内容，支持 click 和 hover 两种触发方式
 * @FilePath: /weryai-web/components/ui/PopMenu.vue
-->
<template>
    <div ref="containerRef" :class="containerClass" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
        <!-- 触发按钮插槽 -->
        <div
            :class="['inline-block', triggerClass]"
            @click.stop="handleClick"
            @mouseenter="handleTriggerMouseEnter"
            @mouseleave="handleTriggerMouseLeave"
        >
            <slot name="trigger">
                <!-- 默认触发按钮 -->
                <button type="button" class="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">
                    Menu
                </button>
            </slot>
        </div>

        <!-- 默认：不 Teleport，保持原有行为 -->
        <template v-if="!props.teleportToBody">
            <!-- hover 模式下的桥接区域，连接触发按钮和菜单 -->
            <div
                v-if="props.trigger === 'hover' && isDropdownOpen"
                ref="bridgeRef"
                :class="['absolute z-[9998]', positionClasses]"
                :style="bridgeStyle"
                @mouseenter="handleBridgeMouseEnter"
            ></div>

            <!-- 下拉菜单内容 -->
            <Transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 transform -translate-y-2 scale-95"
                enter-to-class="opacity-100 transform translate-y-0 scale-100"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 transform translate-y-0 scale-100"
                leave-to-class="opacity-0 transform -translate-y-2 scale-95"
            >
                <div
                    v-if="isDropdownOpen && isPositionReady"
                    ref="menuRef"
                    :class="[
                        'absolute pop-menu z-[9999] bg-[#1c1c20] rounded-lg shadow-[0px_20px_60px_0px_rgba(0,0,0,0.50)] border border-white/10',
                        positionClasses,
                        widthClass,
                    ]"
                    :style="menuStyle"
                    @click.stop="handleMenuClick"
                    @mouseenter="handleMenuMouseEnter"
                    @mouseleave="handleMenuMouseLeave"
                >
                    <slot name="content">
                        <div class="p-2 text-white text-sm">菜单内容</div>
                    </slot>
                </div>
            </Transition>

            <!-- 隐藏的测量元素，用于计算菜单尺寸 -->
            <div
                v-if="isDropdownOpen && !isPositionReady"
                ref="measureRef"
                :class="[
                    'absolute pop-menu z-[9999] bg-[#1c1c20] rounded-lg border border-white/10 invisible',
                    positionClasses,
                    widthClass,
                ]"
                :style="{ marginTop: `${props.gap}px` }"
            >
                <slot name="content">
                    <div class="p-2 text-white text-sm">菜单内容</div>
                </slot>
            </div>
        </template>

        <!-- 可选：Teleport 到 body，解决跨 stacking context 遮挡 -->
        <Teleport v-else to="body">
            <div
                v-if="props.trigger === 'hover' && isDropdownOpen"
                ref="bridgeRef"
                class="fixed"
                :style="teleportedBridgeStyle"
                @mouseenter="handleBridgeMouseEnter"
            ></div>

            <Transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 transform -translate-y-2 scale-95"
                enter-to-class="opacity-100 transform translate-y-0 scale-100"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 transform translate-y-0 scale-100"
                leave-to-class="opacity-0 transform -translate-y-2 scale-95"
            >
                <div
                    v-if="isDropdownOpen && isPositionReady"
                    ref="menuRef"
                    :class="[
                        'fixed pop-menu bg-[#1c1c20] rounded-lg shadow-[0px_20px_60px_0px_rgba(0,0,0,0.50)] border border-white/10',
                        widthClass,
                    ]"
                    :style="menuStyle"
                    @click.stop="handleMenuClick"
                    @mouseenter="handleMenuMouseEnter"
                    @mouseleave="handleMenuMouseLeave"
                >
                    <slot name="content">
                        <div class="p-2 text-white text-sm">菜单内容</div>
                    </slot>
                </div>
            </Transition>

            <div
                v-if="isDropdownOpen && !isPositionReady"
                ref="measureRef"
                :class="[
                    'fixed pop-menu bg-[#1c1c20] rounded-lg border border-white/10 invisible',
                    widthClass,
                ]"
                :style="teleportedMeasureStyle"
            >
                <slot name="content">
                    <div class="p-2 text-white text-sm">菜单内容</div>
                </slot>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * Props 定义
 */
interface Props {
    /** 控制菜单显示/隐藏（支持 v-model） */
    modelValue?: boolean
    /** 触发方式：click | hover */
    trigger?: 'click' | 'hover'
    /** 菜单位置：left | right | center | side-right | side-right-bottom | top */
    position?: 'left' | 'right' | 'center' | 'side-right' | 'side-right-bottom' | 'top'
    /** 菜单宽度：auto | min-w-36 | min-w-48 | min-w-64 | 自定义类名 */
    width?: 'auto' | 'min-w-36' | 'min-w-48' | 'min-w-64' | string
    /** 菜单距离触发按钮的间距（单位：px） */
    gap?: number
    /** 是否点击外部自动关闭（仅 click 模式有效） */
    closeOnClickOutside?: boolean
    /** hover 模式下的延迟关闭时间（单位：ms） */
    hoverDelay?: number
    /** 容器类名 */
    containerClass?: string
    /** 是否将菜单渲染到 body，解决跨层级遮挡 */
    teleportToBody?: boolean
    /** 弹层 z-index（teleportToBody=true 时生效） */
    overlayZIndex?: number
    /** 点击菜单内容时自动关闭 */
    closeOnContentClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    trigger: 'click',
    position: 'right',
    width: 'min-w-36',
    gap: 2,
    closeOnClickOutside: true,
    hoverDelay: 200,
    containerClass: 'relative',
    teleportToBody: false,
    overlayZIndex: 3000,
    closeOnContentClick: false,
})

/**
 * Emits 定义
 */
const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    open: []
    close: []
}>()

/**
 * 响应式状态
 */
const containerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const measureRef = ref<HTMLElement | null>(null) // 用于测量尺寸的隐藏元素
const bridgeRef = ref<HTMLElement | null>(null)
const isDropdownOpen = ref(props.modelValue)
const isPositionReady = ref(false) // 位置是否计算完成
const isMouseInMenu = ref(false) // 跟踪鼠标是否在菜单区域内
const adjustedLeft = ref<number | null>(null) // 边界调整后的左偏移
const teleportedLeft = ref<number | null>(null)
const teleportedTop = ref<number | null>(null)
const teleportedBridgeRect = ref<{
    left: number
    top: number
    width: number
    height: number
} | null>(null)
let hoverTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 计算属性：菜单位置类名
 */
const positionClasses = computed(() => {
    const classes: Record<string, string> = {
        left: 'left-0 top-full',
        right: 'right-0 top-full',
        center: 'left-1/2 -translate-x-1/2 top-full',
        'side-right': 'left-full top-0',
        'side-right-bottom': 'left-full bottom-0',
        'top': 'right-0 bottom-full',
    }
    return classes[props.position] || classes.right
})

/**
 * 计算属性：菜单宽度类名
 */
const widthClass = computed(() => {
    // 如果是预设的宽度值，直接返回
    if (['auto', 'min-w-36', 'min-w-48', 'min-w-64'].includes(props.width)) {
        return props.width === 'auto' ? '' : props.width
    }
    // 否则返回自定义类名
    return props.width
})

/**
 * 计算属性：菜单样式
 */
const menuStyle = computed(() => {
    if (props.teleportToBody) {
        return {
            left: `${teleportedLeft.value ?? 0}px`,
            top: `${teleportedTop.value ?? 0}px`,
            zIndex: String((props.overlayZIndex || 3000) + 1),
        }
    }

    const style: Record<string, string> = {}

    if (props.position === 'side-right' || props.position === 'side-right-bottom') {
        style.marginLeft = `${props.gap}px`
    } else {
        style.marginTop = `${props.gap}px`
    }

    // 如果有边界调整的偏移量，应用它
    if (adjustedLeft.value !== null) {
        style.left = `${adjustedLeft.value}px`
        style.transform = 'none' // 取消 center 模式下的 translateX
    }

    return style
})

const teleportedMeasureStyle = computed(() => {
    if (!props.teleportToBody || !containerRef.value) {
        return {
            left: '0px',
            top: '0px',
            zIndex: String((props.overlayZIndex || 3000) + 1),
        }
    }
    const rect = containerRef.value.getBoundingClientRect()
    return {
        left: `${rect.left}px`,
        top: `${rect.bottom + props.gap}px`,
        zIndex: String((props.overlayZIndex || 3000) + 1),
    }
})

/**
 * 计算属性：桥接区域样式（hover 模式下使用）
 */
const bridgeStyle = computed(() => {
    if (props.teleportToBody) {
        const bridge = teleportedBridgeRect.value
        return {
            left: `${bridge?.left ?? 0}px`,
            top: `${bridge?.top ?? 0}px`,
            width: `${bridge?.width ?? 0}px`,
            height: `${bridge?.height ?? 0}px`,
            zIndex: String(props.overlayZIndex || 3000),
            pointerEvents: 'auto' as const,
        }
    }

    if (props.trigger !== 'hover') {
        return {}
    }
    if (props.position === 'side-right' || props.position === 'side-right-bottom') {
        return {
            left: '100%',
            top: '0',
            width: `${props.gap}px`,
            height: '100%',
            pointerEvents: 'auto' as const,
        }
    }
    if (props.position === 'top') {
        return {
            bottom: '100%',
            height: `${props.gap}px`,
            width: '100%',
            pointerEvents: 'auto' as const,
        }
    }
    return {
        top: '100%',
        height: `${props.gap}px`,
        width: '100%',
        pointerEvents: 'auto' as const,
    }
})

const teleportedBridgeStyle = computed(() => {
    const bridge = teleportedBridgeRect.value
    return {
        left: `${bridge?.left ?? 0}px`,
        top: `${bridge?.top ?? 0}px`,
        width: `${bridge?.width ?? 0}px`,
        height: `${bridge?.height ?? 0}px`,
        zIndex: String(props.overlayZIndex || 3000),
        pointerEvents: 'auto' as const,
    }
})

/**
 * 计算属性：触发区域类名
 */
const triggerClass = computed(() => {
    return props.trigger === 'hover' ? 'cursor-pointer' : ''
})

/**
 * 清除 hover 延迟定时器
 */
const clearHoverTimer = () => {
    if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
    }
}

/**
 * 计算并调整菜单位置，使用隐藏的测量元素获取准确尺寸
 */
const calculateMenuPosition = () => {
    nextTick(() => {
        if (!isDropdownOpen.value) {
            return
        }
        if (!containerRef.value || !measureRef.value) {
            // 如果没有需要调整的元素，直接显示
            isPositionReady.value = true
            return
        }

        const containerRect = containerRef.value.getBoundingClientRect()
        const menuRect = measureRef.value.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const padding = 8 // 距离屏幕边缘的最小间距
        const menuWidth = menuRect.width

        if (props.teleportToBody) {
            let expectedLeft: number
            if (props.position === 'center') {
                expectedLeft = containerRect.left + containerRect.width / 2 - menuWidth / 2
            } else if (props.position === 'left') {
                expectedLeft = containerRect.left
            } else if (
                props.position === 'side-right' ||
                props.position === 'side-right-bottom'
            ) {
                expectedLeft = containerRect.right + props.gap
            } else {
                // right / top
                expectedLeft = containerRect.right - menuWidth
            }

            const maxLeft = Math.max(padding, viewportWidth - padding - menuWidth)
            teleportedLeft.value = Math.min(Math.max(expectedLeft, padding), maxLeft)

            if (props.position === 'top') {
                teleportedTop.value = containerRect.top - props.gap - menuRect.height
            } else if (props.position === 'side-right') {
                teleportedTop.value = containerRect.top
            } else if (props.position === 'side-right-bottom') {
                teleportedTop.value = containerRect.bottom - menuRect.height
            } else {
                teleportedTop.value = containerRect.bottom + props.gap
            }

            if (props.trigger === 'hover') {
                if (
                    props.position === 'side-right' ||
                    props.position === 'side-right-bottom'
                ) {
                    teleportedBridgeRect.value = {
                        left: containerRect.right,
                        top: containerRect.top,
                        width: props.gap,
                        height: containerRect.height,
                    }
                } else if (props.position === 'top') {
                    teleportedBridgeRect.value = {
                        left: containerRect.left,
                        top: containerRect.top - props.gap,
                        width: containerRect.width,
                        height: props.gap,
                    }
                } else {
                    teleportedBridgeRect.value = {
                        left: containerRect.left,
                        top: containerRect.bottom,
                        width: containerRect.width,
                        height: props.gap,
                    }
                }
            }

            isPositionReady.value = true
            return
        }

        // 根据 position 计算菜单的预期左边位置
        let expectedLeft: number
        if (props.position === 'center') {
            expectedLeft = containerRect.left + containerRect.width / 2 - menuWidth / 2
        } else if (props.position === 'left') {
            expectedLeft = containerRect.left
        } else {
            expectedLeft = containerRect.right - menuWidth
        }

        const expectedRight = expectedLeft + menuWidth

        // 检测左边界
        if (expectedLeft < padding) {
            const offset = padding - expectedLeft
            if (props.position === 'center') {
                const currentLeft = containerRect.width / 2 - menuWidth / 2
                adjustedLeft.value = currentLeft + offset
            } else if (props.position === 'left') {
                adjustedLeft.value = offset
            }
        }
        // 检测右边界
        else if (expectedRight > viewportWidth - padding) {
            const offset = expectedRight - (viewportWidth - padding)
            if (props.position === 'center') {
                const currentLeft = containerRect.width / 2 - menuWidth / 2
                adjustedLeft.value = currentLeft - offset
            }
        } else {
            adjustedLeft.value = null
        }

        // 位置计算完成，显示真正的菜单
        isPositionReady.value = true
    })
}

/**
 * 处理点击事件
 */
const handleClick = () => {
    if (props.trigger === 'click') {
        toggleDropdown()
    }
}

/**
 * 处理触发按钮鼠标进入（hover 模式）
 */
const handleTriggerMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
        openDropdown()
    }
}

/**
 * 处理触发按钮鼠标离开（hover 模式）
 */
const handleTriggerMouseLeave = () => {
    if (props.trigger === 'hover') {
        // 如果鼠标已经在菜单上，不关闭
        if (isMouseInMenu.value) {
            return
        }
        // 延迟关闭，给用户时间移动到菜单
        hoverTimer = setTimeout(() => {
            if (!isMouseInMenu.value) {
                closeDropdown()
            }
        }, props.hoverDelay)
    }
}

/**
 * 处理桥接区域鼠标进入（hover 模式）
 */
const handleBridgeMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
    }
}

/**
 * 处理菜单鼠标进入（hover 模式）
 */
const handleMenuMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
        isMouseInMenu.value = true
    }
}

/**
 * 处理菜单鼠标离开（hover 模式）
 */
const handleMenuMouseLeave = () => {
    if (props.trigger === 'hover') {
        isMouseInMenu.value = false
        hoverTimer = setTimeout(() => {
            closeDropdown()
        }, props.hoverDelay)
    }
}

/**
 * 处理菜单内容点击（可选自动关闭）
 */
const handleMenuClick = () => {
    if (props.closeOnContentClick) {
        closeDropdown()
    }
}

/**
 * 处理容器鼠标进入（hover 模式）
 */
const handleMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
    }
}

/**
 * 处理容器鼠标离开（hover 模式）
 */
const handleMouseLeave = () => {
    if (props.trigger === 'hover') {
        // 如果鼠标在菜单上，不关闭
        if (isMouseInMenu.value) {
            return
        }
        hoverTimer = setTimeout(() => {
            if (!isMouseInMenu.value) {
                closeDropdown()
            }
        }, props.hoverDelay)
    }
}

/**
 * 切换下拉菜单显示状态
 */
const toggleDropdown = () => {
    if (isDropdownOpen.value) {
        closeDropdown()
    } else {
        openDropdown()
    }
}

/**
 * 打开下拉菜单
 */
const openDropdown = () => {
    isDropdownOpen.value = true
    isPositionReady.value = false // 重置位置状态
    updateModelValue()
    emit('open')
    // 计算位置
    calculateMenuPosition()
}

/**
 * 关闭下拉菜单
 */
const closeDropdown = () => {
    isDropdownOpen.value = false
    isPositionReady.value = false // 重置位置状态
    isMouseInMenu.value = false // 重置菜单内状态
    adjustedLeft.value = null // 重置位置调整
    teleportedLeft.value = null
    teleportedTop.value = null
    teleportedBridgeRect.value = null
    updateModelValue()
    emit('close')
}

/**
 * 更新 v-model 值
 */
const updateModelValue = () => {
    emit('update:modelValue', isDropdownOpen.value)
}

/**
 * 点击外部区域关闭菜单
 */
const handleClickOutside = (event: MouseEvent) => {
    if (!props.closeOnClickOutside || !isDropdownOpen.value) {
        return
    }

    const target = event.target as HTMLElement
    if (
        containerRef.value &&
        !containerRef.value.contains(target) &&
        menuRef.value &&
        !menuRef.value.contains(target)
    ) {
        closeDropdown()
    }
}

/**
 * 监听 props.modelValue 变化
 */
watch(
    () => props.modelValue,
    newValue => {
        isDropdownOpen.value = newValue
    }
)

/**
 * 生命周期：挂载时添加点击外部监听
 */
onMounted(() => {
    if (props.closeOnClickOutside && props.trigger === 'click') {
        document.addEventListener('click', handleClickOutside)
    }
    if (props.teleportToBody) {
        window.addEventListener('resize', calculateMenuPosition)
        window.addEventListener('scroll', calculateMenuPosition, true)
    }
})

/**
 * 生命周期：卸载时移除点击外部监听和清理定时器
 */
onUnmounted(() => {
    if (props.closeOnClickOutside && props.trigger === 'click') {
        document.removeEventListener('click', handleClickOutside)
    }
    if (props.teleportToBody) {
        window.removeEventListener('resize', calculateMenuPosition)
        window.removeEventListener('scroll', calculateMenuPosition, true)
    }
    clearHoverTimer()
})

/**
 * 暴露方法供外部调用
 */
defineExpose({
    open: openDropdown,
    close: closeDropdown,
    toggle: toggleDropdown,
    isOpen: () => isDropdownOpen.value,
})
</script>

<style scoped lang="less">
.pop-menu-no-use::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(180deg,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 100%);
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: 1;
    pointer-events: none;
}
</style>
